import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
];

// Days per month (ignoring leap year — Feb always shows 29)
const DAYS_IN_MONTH = {
  '01': 31, '02': 29, '03': 31, '04': 30, '05': 31, '06': 30,
  '07': 31, '08': 31, '09': 30, '10': 31, '11': 30, '12': 31
};

/**
 * MonthDayPicker — Two-dropdown picker for month and day (no year).
 * Stores/accepts value in MM-DD format (e.g., "03-14").
 *
 * @param {Object} props
 * @param {string} props.value - Current value in MM-DD format, or empty string
 * @param {Function} props.onChange - Called with MM-DD string when both selected, or '' when cleared
 * @param {boolean} [props.disabled] - Whether the picker is disabled
 * @param {string} [props.className] - Additional classes for the container
 */
const MonthDayPicker = ({ value, onChange, disabled = false, className = '' }) => {
  // Internal state for partial selections (month chosen but day not yet)
  const [internalMonth, setInternalMonth] = useState('');
  const [internalDay, setInternalDay] = useState('');

  // Sync internal state from controlled value prop
  useEffect(() => {
    if (value && /^\d{2}-\d{2}$/.test(value)) {
      const [m, d] = value.split('-');
      setInternalMonth(m);
      setInternalDay(d);
    } else {
      setInternalMonth('');
      setInternalDay('');
    }
  }, [value]);

  const currentMonth = internalMonth;
  const currentDay = internalDay;
  const maxDays = currentMonth ? (DAYS_IN_MONTH[currentMonth] || 31) : 31;

  const handleMonthChange = (newMonth) => {
    if (!newMonth) {
      setInternalMonth('');
      setInternalDay('');
      onChange('');
      return;
    }
    setInternalMonth(newMonth);
    // Clamp day if it exceeds new month's max
    const newMax = DAYS_IN_MONTH[newMonth] || 31;
    let day = currentDay;
    if (day && parseInt(day, 10) > newMax) {
      day = String(newMax).padStart(2, '0');
      setInternalDay(day);
    }
    // Only emit onChange when both month and day are selected
    if (day) {
      onChange(`${newMonth}-${day}`);
    }
    // Otherwise just update internal month — wait for day selection
  };

  const handleDayChange = (newDay) => {
    if (!newDay) {
      setInternalDay('');
      onChange('');
      return;
    }
    setInternalDay(newDay);
    const month = currentMonth || '01';
    if (!currentMonth) setInternalMonth('01');
    onChange(`${month}-${newDay}`);
  };

  const handleClear = () => {
    setInternalMonth('');
    setInternalDay('');
    onChange('');
  };

  const dayOptions = [];
  for (let d = 1; d <= maxDays; d++) {
    dayOptions.push(String(d).padStart(2, '0'));
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <select
        value={currentMonth}
        onChange={(e) => handleMonthChange(e.target.value)}
        disabled={disabled}
        className={`flex-1 px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base ${
          disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'
        }`}
      >
        <option value="">Month...</option>
        {MONTHS.map(m => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </select>

      <select
        value={currentDay}
        onChange={(e) => handleDayChange(e.target.value)}
        disabled={disabled || !currentMonth}
        className={`w-20 px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base ${
          (disabled || !currentMonth) ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'
        }`}
      >
        <option value="">Day</option>
        {dayOptions.map(d => (
          <option key={d} value={d}>{parseInt(d, 10)}</option>
        ))}
      </select>

      {value && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          className="p-1 text-gray-300 hover:text-red-500 transition-colors rounded"
          title="Clear date"
          aria-label="Clear date"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};

export default MonthDayPicker;
